/**
 * PATH: packages/ui/src/kanban/allocation-source.ts
 * FILE_ID: QUI-KANBAN-ALLOC-SOURCE-001
 * PURPOSE: Pure helpers that derive a card's CURRENT allocation source
 *          (USER_MANUAL, AI_AUTO, AI_SUGGESTED_PENDING, …) from the
 *          data the BE already ships with every Card: `audit_log_json`.
 *
 * WHY: Module D of EMAIL_KANBAN_ALLOCATION_PLAN.md. The card face has
 *      to know whether THIS card is an AI suggestion awaiting an
 *      operator's tick / cross. The cheapest way to answer that
 *      question today is to scan the audit log shipped by the BE's
 *      `record_allocation_event` recorder (qb!2809).
 *
 *      A future BE add — `Card.primary_allocation_source` field —
 *      would let us drop the scan; the helpers below check that
 *      field first and fall back to the scan, so the migration
 *      doesn't touch any call sites.
 *
 * FLOW:
 *   getCardAllocationSource(card)
 *      1. read card.primary_allocation_source if present   ← future BE
 *      2. else scan card.audit_log_json from newest → oldest
 *      3. stop on the first event that REPRESENTS the current state
 *         - 'alloc_revoked'         → null  (no active allocation)
 *         - 'alloc_ai_confirmed'    → 'AI_SUGGESTED_USER_CONFIRMED'
 *         - 'alloc_ai_rejected'     → null  (rejected → archived)
 *         - 'alloc_created'         → entry.source
 *
 * Pure I → O. No React, no API calls. Drops in any tenant.
 *
 * DEPENDENCIES:
 *   - ./types : Card, AllocationSource (type-only)
 *
 * RELATED:
 *   - components/CardAiBand.tsx (renders when isCardAiPending)
 *   - components/KanbanLane.tsx (UC|AI tabs derive their bucket here)
 *   - email_kanban/services/allocation_audit.py
 *     (BE recorder that produces the audit_log_json shape we read)
 */

import type { AllocationSource, Card } from './types'

/**
 * Audit-log event names emitted by Module A's
 * `record_allocation_event`. We pin them as literal strings
 * (not an enum) so we can be lenient about unknown future event
 * names — the helpers just keep scanning past them.
 */
export const AUDIT_EVENTS = {
  CREATED: 'alloc_created',
  AI_CONFIRMED: 'alloc_ai_confirmed',
  AI_REJECTED: 'alloc_ai_rejected',
  REVOKED: 'alloc_revoked',
  MOVED: 'alloc_moved',
} as const

/**
 * Future-friendly shape augmentation. When the BE ships
 * `Card.primary_allocation_source` we'll just read that. Today
 * the field is missing on the wire so this is a soft optional.
 */
export interface CardWithMaybePrimarySource {
  primary_allocation_source?: AllocationSource | null
  primary_allocation_id?: string | null
}

/**
 * Returns the active allocation source for the card, or `null` when
 * the card is currently unallocated (revoked / never allocated /
 * AI-rejected). Idempotent + safe on malformed audit logs.
 */
export function getCardAllocationSource(
  card: Card & CardWithMaybePrimarySource,
): AllocationSource | null {
  // Phase 1 — server-side field wins when present.
  if (card.primary_allocation_source !== undefined) {
    return card.primary_allocation_source ?? null
  }

  // Phase 2 — scan audit log. Newest entries are at the END of the
  // array (the BE appends; never re-orders). Walk backwards so we
  // hit the most recent state-changing event first.
  const log = Array.isArray(card.audit_log_json) ? card.audit_log_json : []
  for (let i = log.length - 1; i >= 0; i--) {
    const entry = log[i] as Record<string, unknown> & { event?: unknown }
    const event = typeof entry.event === 'string' ? entry.event : null
    if (!event) continue

    if (event === AUDIT_EVENTS.REVOKED) return null
    if (event === AUDIT_EVENTS.AI_REJECTED) return null
    if (event === AUDIT_EVENTS.AI_CONFIRMED) return 'AI_SUGGESTED_USER_CONFIRMED'
    if (event === AUDIT_EVENTS.CREATED) {
      const source = entry.source
      return isAllocationSource(source) ? source : null
    }
    // 'alloc_moved' and any unknown event names — keep scanning.
  }
  return null
}

/**
 * Returns the allocation id of the current active allocation, or
 * `null` when there is none. Used by `<CardAiBand>` to call
 * confirm/reject without an extra fetch.
 */
export function getCardAllocationId(
  card: Card & CardWithMaybePrimarySource,
): string | null {
  if (card.primary_allocation_id !== undefined) {
    return card.primary_allocation_id ?? null
  }
  const log = Array.isArray(card.audit_log_json) ? card.audit_log_json : []
  for (let i = log.length - 1; i >= 0; i--) {
    const entry = log[i] as Record<string, unknown> & { event?: unknown }
    const event = typeof entry.event === 'string' ? entry.event : null
    if (!event) continue
    if (event === AUDIT_EVENTS.REVOKED) return null
    if (event === AUDIT_EVENTS.AI_REJECTED) return null
    if (
      event === AUDIT_EVENTS.AI_CONFIRMED ||
      event === AUDIT_EVENTS.CREATED
    ) {
      const id = entry.allocation_id
      return typeof id === 'string' && id.length > 0 ? id : null
    }
  }
  return null
}

/** Convenience predicate — the AI band only renders when this is true. */
export function isCardAiPending(
  card: Card & CardWithMaybePrimarySource,
): boolean {
  return getCardAllocationSource(card) === 'AI_SUGGESTED_PENDING'
}

/**
 * Three-way classification of a card list for the Triage UC|AI tabs.
 * Returns a shallow copy of each bucket so the renderer can sort /
 * filter inside without mutating the caller's array.
 */
export function splitCardsByAllocationSource(
  cards: ReadonlyArray<Card & CardWithMaybePrimarySource>,
): {
  uc: Card[]
  ai: Card[]
  /** Everything that isn't AI_SUGGESTED_PENDING — used as the
   *  "All" fallback. Always equals `uc.length + ai-confirmed`. */
  other: Card[]
} {
  const uc: Card[] = []
  const ai: Card[] = []
  const other: Card[] = []
  for (const card of cards) {
    const source = getCardAllocationSource(card)
    if (source === 'AI_SUGGESTED_PENDING') {
      ai.push(card)
    } else if (source === 'USER_MANUAL' || source === null) {
      // Treat unallocated cards (never allocated, or revoked) as UC —
      // the operator created them or moved them to Triage manually.
      uc.push(card)
      other.push(card)
    } else {
      // AI_AUTO / AI_SUGGESTED_USER_CONFIRMED / RULE — these are
      // already "trusted" placements that don't need the operator's
      // tick. They fall under "other" so the Triage UC tab can
      // optionally include them (driven by caller preference).
      other.push(card)
    }
  }
  return { uc, ai, other }
}

// ──────────────────────────────────────────────────────────
// Internals
// ──────────────────────────────────────────────────────────

const VALID_SOURCES = new Set<AllocationSource>([
  'AI_AUTO',
  'AI_SUGGESTED_PENDING',
  'AI_SUGGESTED_USER_CONFIRMED',
  'USER_MANUAL',
  'RULE',
])

function isAllocationSource(value: unknown): value is AllocationSource {
  return typeof value === 'string' && VALID_SOURCES.has(value as AllocationSource)
}
